import { ISocialPlatform } from '@/interfaces/socials'
import React from 'react'
import { ModalPage } from '../ui/modals/ModalPage.component'
import { Title } from '../commons/typography/Title'
import { SocialIconLink } from '../commons/links/SocialIconLink.component'
import { InputComponent } from '../commons/forms/input/Input.component'
import { ButtonComponent, LabelButton } from '../commons'
import { useProfilePage } from '@/shared/hooks/profilePage'
import { cleanUsername } from './misc/utils'
import { getSocialUserUrl } from '@/shared/utils'

export interface OnSaveParams {
  identifier: string
  audience: number
}

interface SocialConfiguratorModalProps {
  platform: ISocialPlatform
  show: boolean
  onSave?: (params: OnSaveParams) => void
  onRemove?: () => void
  close?: () => void
  loading?: boolean
}

export const SocialConfiguratorModal: React.FC<
  SocialConfiguratorModalProps
> = ({ platform, show, onSave, onRemove, close, loading }) => {
  const { profile } = useProfilePage()
  const [identifier, setIdentifier] = React.useState<string>('')
  const [audience, setAudience] = React.useState<number>(0)
  const [errorAudience, setErrorAudience] = React.useState<boolean>(false)
  const [errorIdentifier, setErrorIdentifier] = React.useState<boolean>(false)

  const currentSocial = React.useMemo(() => {
    return profile?.socials?.find((social) => social.platform === platform)
  }, [profile, platform])

  React.useEffect(() => {
    setIdentifier(currentSocial?.identifier || '')
    setAudience(currentSocial?.audience || 0)
  }, [currentSocial])

  const platformName = platform?.charAt(0).toUpperCase() + platform?.slice(1)

  const onChangeIdentifier = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setErrorIdentifier(false)
    setIdentifier(cleanUsername(value))
  }

  const onChangeAudience = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target
    setErrorAudience(false)
    setAudience(parseInt(value))
  }

  const saveData = () => {
    if (!identifier) {
      setErrorIdentifier(true)
      return
    }
    if (audience < 0) {
      setErrorAudience(true)
      return
    }

    onSave &&
      onSave({
        identifier,
        audience,
      })
  }

  const remove = () => {
    if (loading) return
    onRemove && onRemove()
  }

  return (
    <ModalPage
      show={show}
      className={['full-w p-0']}
      classNameContainer={['full-w p-0']}
      hideCloseIcon={loading}
      onClose={close}
    >
      <div className="py-40 px-24 px-64--tablet py-64--tablet flex flex-column gap-32 gap-40--tablet items-center justify-center">
        <Title
          sizeClass="text-20 text-24--tablet text-32--desktop"
          className="text-center"
        >
          Add {platformName}
        </Title>

        <div className="flex flex-column gap-16 items-center">
          <SocialIconLink
            social={platform}
            size={50}
            username={identifier}
            filterGray={!currentSocial}
          />
          <a
            href={`https://${platform}.com/${identifier}`}
            target="_blank"
            className="text-14 text-info text-center"
            rel="noreferrer noopener"
          >
            {getSocialUserUrl(platform, identifier)}
          </a>
        </div>

        <InputComponent
          size="small"
          placeholder="Username (Please enter username only, no URLs)"
          value={identifier}
          error={errorIdentifier ? 'Username is required' : undefined}
          onChange={onChangeIdentifier}
        />

        <InputComponent
          type="number"
          size="small"
          placeholder="Followers Count"
          value={audience}
          error={errorAudience ? 'Followers count is required' : undefined}
          onChange={onChangeAudience}
        />

        <ButtonComponent
          loading={loading}
          disabled={loading}
          size="small"
          fullWidth
          label="Save account"
          onClick={saveData}
        />

        {currentSocial && (
          <div className="cursor-pointer" onClick={remove}>
            <LabelButton
              label="Remove account"
              className={['text-14', loading ? 'text-tertirary' : '']}
            />
          </div>
        )}
      </div>
    </ModalPage>
  )
}
